import { Divider, Text, Box, Heading } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import { FloatingHeadTable } from 'components/Table';
import ExportMenu from 'containers/Reports/shared/ExportMenu';
import useStickyHeader from 'containers/Reports/shared/useStickyHeader';
import { useUserObject } from 'store/useUserStore';
import { formatCurrency, formatDistanceUnit } from 'utils/format';
import { isDistanceInMiles } from 'utils/helpers';

const DailyFlatRateByTotalTable: React.FC<{ data: any; dateRange: any[] }> = ({ data, dateRange }) => {
  const { t } = useTranslation(['common', 'reports']);
  useStickyHeader();

  const userStore = useUserObject();

  const columns = useMemo(
    () => [
      {
        Header: t('common:name'),
        accessor: 'name',
        width: 200,
      },
      {
        Header: isDistanceInMiles() ? t('reports:totalMileage') : t('reports:totalKilometers'),
        width: 140,
        accessor: 'totalMileage',
        Cell: (data: CellValue) => (
          <Text>
            {data.row.original?.results?.total_mileage
              ? formatDistanceUnit(+data.row.original?.results?.total_mileage)
              : '- '}
          </Text>
        ),
      },
      {
        Header: t('reports:regularHrs'),
        accessor: 'regularTime',
        width: 120,
        Cell: (data: CellValue) => <Text>{data.row.original?.results?.regular_hours || '- '}</Text>,
      },
      {
        Header: t('reports:regularPay'),
        accessor: 'regularCost',
        width: 120,
        Cell: (data: CellValue) => (
          <Text>
            {data.row.original?.results?.regular_pay ? formatCurrency(+data.row.original?.results?.regular_pay) : '- '}
          </Text>
        ),
      },
      ...(userStore?.companyInfo?.daily_overtime
        ? [
            {
              Header: t('reports:OTHrs'),
              accessor: 'overtime',
              width: 120,
              Cell: (data: CellValue) => <Text>{data.row.original?.results?.daily_overtime_hours || '- '}</Text>,
            },
            {
              Header: t('reports:OTCost'),
              accessor: 'overTimeCost',
              width: 120,
              Cell: (data: CellValue) => (
                <Text>
                  {data.row.original?.results?.overtime_cost
                    ? formatCurrency(data.row.original?.results?.overtime_cost)
                    : '- '}
                </Text>
              ),
            },
          ]
        : []),

      {
        Header: t('reports:totalTime'),
        accessor: 'totalTime',
        width: 120,
        Cell: (data: CellValue) => <Text>{data.row.original?.results?.total_time || '- '}</Text>,
      },
      {
        Header: t('reports:totalDays'),
        accessor: 'totalDays',
        width: 120,
        Cell: (data: CellValue) => <Text>{data.row.original?.results?.total_days || '- '}</Text>,
      },
      {
        Header: t('reports:totalCost'),
        accessor: 'totalCost',
        width: 120,
        Cell: (data: CellValue) => (
          <Text>
            {data.row.original?.results?.total_cost ? formatCurrency(data.row.original?.results?.total_cost) : '- '}
          </Text>
        ),
      },
      {
        Header: isDistanceInMiles() ? t('reports:totalMileageCost') : t('reports:totalKilometersCost'),
        accessor: 'totalMileageCost',
        width: 200,
        Cell: (data: CellValue) => (
          <Text>
            {data.row.original?.results?.total_mileage_cost
              ? formatCurrency(data.row.original?.results?.total_mileage_cost)
              : '- '}
          </Text>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <>
      <Divider my="4" />
      <Box p="4">
        <Heading variant="h2" size="md" fontWeight="semibold" mb="4">
          {t('reports:searchResults')}
        </Heading>
        {data?.payload?.attributes ? (
          <ExportMenu
            attributes={data?.payload?.attributes}
            dateRange={dateRange}
            reportType="byTotalDaily"
            displaySettings={data?.payload?.integrations}
          />
        ) : null}
        <FloatingHeadTable columns={columns} data={data?.payload?.users || []} />
      </Box>
    </>
  );
};

export default DailyFlatRateByTotalTable;
